import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { Linkedin, Twitter, Youtube } from 'lucide-react'
import { Logo } from '@/components/generals/logo'
import { Container } from '@/components/generals/container'
import { FOOTER_NAV, LEGAL_NAV } from '@/constants/navigation'
import { siteConfig } from '@/constants/site'

const SOCIAL = [
    { label: 'Veyra on LinkedIn', href: siteConfig.links.linkedin, Icon: Linkedin },
    { label: 'Veyra on X', href: siteConfig.links.x, Icon: Twitter },
    { label: 'Veyra on YouTube', href: siteConfig.links.youtube, Icon: Youtube }
]

export const SiteFooter: FC = (): ReactNode => {
    return (
        <footer className='relative overflow-hidden bg-burgundy text-cream'>
            <Container>
                <div className='grid gap-14 border-t border-cream/10 pb-6 pt-16 lg:grid-cols-12 lg:pt-20'>
                    <div className='lg:col-span-4'>
                        <Link href='/' aria-label='Veyra, back to home' className='inline-block rounded-md'>
                            <Logo tone='light' />
                        </Link>

                        <p className='mt-6 max-w-xs text-sm leading-relaxed text-cream/65'>
                            AI-powered financial management for teams that want clarity, not more spreadsheets.
                        </p>

                        <ul className='mt-8 flex items-center gap-2'>
                            {SOCIAL.map(({ label, href, Icon }) => (
                                <li key={label}>
                                    <a
                                        href={href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        aria-label={label}
                                        className='flex size-10 items-center justify-center rounded-full border border-cream/15 text-cream/75 transition-colors duration-300 hover:border-cream/40 hover:text-cream'
                                    >
                                        <Icon aria-hidden='true' className='size-4' />
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <nav aria-label='Footer' className='grid grid-cols-2 gap-x-8 gap-y-12 sm:grid-cols-4 lg:col-span-8'>
                        {FOOTER_NAV.map(group => (
                            <div key={group.title}>
                                <h2 className='font-mono text-[11px] font-medium uppercase tracking-[0.2em] text-beige'>
                                    {group.title}
                                </h2>

                                <ul className='mt-5 space-y-3'>
                                    {group.links.map(link => (
                                        <li key={link.href}>
                                            <Link href={link.href} className='text-sm text-cream/70 transition-colors duration-300 hover:text-cream'>
                                                {link.label}
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </nav>
                </div>

                <div
                    aria-hidden='true'
                    className='pointer-events-none mt-10 select-none font-heading text-[27vw] font-medium leading-[0.78] tracking-[-0.06em] text-cream/[0.07] lg:text-[21rem]'
                >
                    Veyra
                </div>

                <div className='flex flex-col gap-4 border-t border-cream/10 py-7 text-xs text-cream/55 md:flex-row md:items-center md:justify-between'>
                    <p suppressHydrationWarning>
                        &copy; {new Date().getFullYear()} {siteConfig.legalName} All rights reserved.
                    </p>

                    <ul className='flex flex-wrap items-center gap-x-6 gap-y-2'>
                        {LEGAL_NAV.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='transition-colors duration-300 hover:text-cream'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}

                        <li className='text-cream/40'>
                            Backed by Y Combinator
                        </li>
                    </ul>
                </div>
            </Container>
        </footer>
    )
}
